import { Button, ButtonLink } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import type { CompanySearchResult } from '@dash-lite/sdk';
import { ArrowRight, Building2, MapPin, Search, SearchX } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, Badge, EmptyState, Skeleton, Spinner } from '@/components/ui/primitives';
import { MIN_QUERY_LENGTH, useCompanySearch } from '@/hooks/use-company-search';
import { useSession } from '@/hooks/use-session';
import { useSite } from '@/hooks/use-site';
import { formatCountry } from '@/lib/format';
import { paths } from '@/lib/paths';

/**
 * Où mène un résultat de recherche ?
 *
 * Au **paiement**, avec l'entreprise en contexte d'achat : le visiteur passe
 * de la recherche à la carte bancaire sans écran intermédiaire, l'aperçu
 * public de l'entreprise s'affichant à côté du formulaire. Seul un visiteur
 * dont l'accès est déjà ouvert va directement à la fiche complète — lui
 * proposer de payer une seconde fois n'aurait aucun sens.
 *
 * La règle est écrite ici, une fois, plutôt que dans chaque écran qui affiche
 * des résultats.
 */
function destinationFor(registrationNumber: string, hasAccess: boolean): string {
    return hasAccess ? paths.company(registrationNumber) : paths.checkoutFor(registrationNumber);
}

/**
 * Champ de recherche d'entreprises, avec sa liste de résultats.
 *
 * Deux usages :
 *
 * - `variant="hero"` : la barre proéminente de la page d'accueil, avec ses
 *   résultats en superposition ;
 * - `variant="page"` : la version de la page de recherche, résultats en flux.
 *
 * Le site peut exiger une session pour rechercher : `site.search_requires_auth`
 * (`GET /site`) le dit. La plupart des sites laissent la recherche publique et
 * ne réservent que la fiche complète — c'est la configuration par défaut.
 */
export function CompanySearch({
    variant = 'page',
    autoFocus = false,
    initialQuery = '',
    onQueryChange,
}: {
    variant?: 'hero' | 'page';
    autoFocus?: boolean;
    initialQuery?: string;
    onQueryChange?: (query: string) => void;
}) {
    const site = useSite();
    const { isAuthenticated, hasAccess } = useSession();
    const navigate = useNavigate();

    const [query, setQuery] = useState(initialQuery);

    const searchLocked = site.search_requires_auth && !isAuthenticated;
    const search = useCompanySearch(query, { enabled: !searchLocked });

    const showPanel = query.trim().length >= MIN_QUERY_LENGTH;

    function handleChange(value: string): void {
        setQuery(value);
        onQueryChange?.(value);
    }

    if (searchLocked) {
        return (
            <Alert tone="info" title="La recherche demande une connexion">
                Ce site a activé l'option « recherche réservée aux comptes ».{' '}
                <ButtonLink to={paths.login} size="sm" variant="secondary" className="mt-3">
                    Se connecter
                </ButtonLink>
            </Alert>
        );
    }

    return (
        <div className={cn('relative w-full', variant === 'hero' && 'max-w-2xl')}>
            <form
                role="search"
                onSubmit={(event) => {
                    event.preventDefault();

                    // Entrée ouvre le premier résultat : le raccourci attendu
                    // quand on a tapé un nom précis.
                    const first = search.results[0];

                    if (first) {
                        navigate(destinationFor(first.registration_number, hasAccess), {
                            state: { preview: first },
                        });
                    }
                }}
                className={cn(
                    'border-line-strong bg-surface flex items-center gap-2 rounded-xl border shadow-sm transition-shadow focus-within:shadow-md',
                    variant === 'hero' ? 'p-2' : 'p-1.5',
                )}
            >
                <Search className="text-ink-muted ml-2 size-5 shrink-0" aria-hidden />

                <input
                    data-dashlite="company-search"
                    type="search"
                    autoFocus={autoFocus}
                    value={query}
                    onChange={(event) => handleChange(event.target.value)}
                    placeholder="Nom de l'entreprise ou numéro d'immatriculation"
                    aria-label="Rechercher une entreprise"
                    className={cn(
                        'text-ink placeholder:text-ink-muted/70 min-w-0 flex-1 bg-transparent outline-none',
                        variant === 'hero' ? 'py-2.5 text-base' : 'py-2 text-sm',
                    )}
                />

                {search.loading && <Spinner className="mr-1" />}

                <Button
                    data-dashlite="company-search-submit"
                    type="submit"
                    size={variant === 'hero' ? 'md' : 'sm'}
                    disabled={search.results.length === 0}
                >
                    Rechercher
                </Button>
            </form>

            <p className="text-ink-muted mt-2 text-xs">
                Recherche par nom ou par identifiant — {formatCountry(site.country)}. Le pays est déduit du site : aucun
                code pays n'est transmis à l'API.
            </p>

            {showPanel && (
                <div
                    className={cn(
                        variant === 'hero' &&
                            'border-line bg-surface absolute inset-x-0 top-full z-20 mt-3 max-h-96 overflow-y-auto rounded-xl border p-2 shadow-xl',
                        variant === 'page' && 'mt-6',
                    )}
                >
                    <SearchResults
                        results={search.results}
                        loading={search.loading}
                        empty={search.empty}
                        error={search.error}
                        query={search.submittedQuery}
                        compact={variant === 'hero'}
                    />
                </div>
            )}
        </div>
    );
}

/** Liste des résultats, ou l'état qui en tient lieu (chargement, panne, vide). */
export function SearchResults({
    results,
    loading,
    empty,
    error,
    query,
    compact = false,
}: {
    results: CompanySearchResult[];
    loading: boolean;
    empty: boolean;
    error: ReturnType<typeof useCompanySearch>['error'];
    query: string;
    compact?: boolean;
}) {
    if (error) {
        return (
            <Alert tone={error.retryable ? 'caution' : 'critical'} title={error.message}>
                {error.hint}
            </Alert>
        );
    }

    if (loading && results.length === 0) {
        return (
            <div className="space-y-2">
                {[0, 1, 2].map((index) => (
                    <Skeleton key={index} className="h-16 w-full" />
                ))}
            </div>
        );
    }

    if (empty) {
        return (
            <EmptyState
                icon={<SearchX className="size-8" />}
                title={`Aucune entreprise ne correspond à « ${query} »`}
                description="Une recherche sans correspondance renvoie simplement une liste vide — ce n'est pas une erreur. Essayez un autre nom, ou saisissez directement l'identifiant national."
            />
        );
    }

    return (
        <ul className={cn('space-y-2', compact && 'space-y-1')}>
            {results.map((company) => (
                <li key={company.registration_number} data-dashlite="company-result">
                    <CompanyResultRow company={company} compact={compact} />
                </li>
            ))}
        </ul>
    );
}

function CompanyResultRow({ company, compact }: { company: CompanySearchResult; compact: boolean }) {
    const { hasAccess } = useSession();

    return (
        <ButtonLink
            to={destinationFor(company.registration_number, hasAccess)}
            // Le résultat de recherche est transporté dans l'état de
            // navigation : l'écran suivant affiche le nom et la ville
            // immédiatement, sans avoir à réinterroger la recherche.
            state={{ preview: company }}
            variant="ghost"
            className={cn(
                'border-line hover:border-brand-200 hover:bg-brand-50/50 h-auto w-full justify-between rounded-lg border px-4 text-left',
                compact ? 'py-2.5' : 'py-3.5',
            )}
        >
            <span className="flex min-w-0 items-center gap-3">
                <span className="bg-brand-50 text-brand-600 flex size-9 shrink-0 items-center justify-center rounded-lg">
                    <Building2 className="size-4" aria-hidden />
                </span>

                <span className="min-w-0">
                    <span className="text-ink block truncate font-medium">{company.name}</span>
                    <span className="text-ink-muted mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs">
                        <span className="font-mono">{company.registration_number}</span>
                        {company.city && (
                            <span className="inline-flex items-center gap-1">
                                <MapPin className="size-3" aria-hidden />
                                {company.city}
                            </span>
                        )}
                        {company.legal_form && !compact && <span className="truncate">{company.legal_form}</span>}
                    </span>
                </span>
            </span>

            <span className="flex shrink-0 items-center gap-2">
                {!compact && <Badge tone="neutral">{company.country}</Badge>}
                <ArrowRight className="text-ink-muted size-4" aria-hidden />
            </span>
        </ButtonLink>
    );
}
